import type { AdvisorMovement } from "@/types";
import { MOVEMENT_META } from "@/pages/admin/consignaciones/components/movementMeta";
import { formatCOP, formatDate, formatNumber } from "@/utils/format";

interface AdvisorAccountPanelProps {
  compradoValue: number;
  compradoUnits: number;
  consignadoValue: number;
  consignedUnits: number;
  debt: number;
  movements: AdvisorMovement[];
}

// Cuenta corriente del asesor: lo que compró, lo que tiene en consignación y lo que debe.
const AdvisorAccountPanel = ({
  compradoValue,
  compradoUnits,
  consignadoValue,
  consignedUnits,
  debt,
  movements,
}: AdvisorAccountPanelProps) => {
  const cards = [
    {
      icon: "ri-shopping-bag-3-line",
      label: "Compraste al admin",
      value: formatCOP(compradoValue),
      hint: `${formatNumber(compradoUnits)} unidades · ya son tuyas`,
      bubble: "bg-primary-500/25 text-primary-100",
    },
    {
      icon: "ri-hand-heart-line",
      label: "Tenencia (en consignación)",
      value: formatCOP(consignadoValue),
      hint: `${formatNumber(consignedUnits)} unidades en custodia · no es deuda`,
      bubble: "bg-secondary-500/25 text-secondary-200",
    },
    {
      icon: "ri-hand-coin-line",
      label: "Deuda (por ventas)",
      value: formatCOP(debt),
      hint: debt > 0 ? "Lo que debes por lo que vendiste" : "Sin saldo pendiente",
      bubble: "bg-accent-500/20 text-accent-200",
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
        {cards.map((c) => (
          <div key={c.label} className="glass-panel rounded-lg p-4 animate-fade-up">
            <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${c.bubble}`}>
              <i className={`${c.icon} text-lg`} />
            </span>
            <p className="text-[11px] text-foreground-500 mt-3">{c.label}</p>
            <p className="text-lg md:text-xl font-heading font-extrabold text-foreground-950 mt-0.5 break-words">
              {c.value}
            </p>
            <p className="text-[10px] text-foreground-500 mt-1">{c.hint}</p>
          </div>
        ))}
      </div>

      <div className="glass-panel rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-accent-500/15 flex items-center gap-2">
          <i className="ri-history-line text-accent-300" />
          <h3 className="editable-title text-sm font-bold">Movimientos de tu cuenta</h3>
        </div>
        {movements.length === 0 ? (
          <p className="px-5 py-8 text-center text-xs text-foreground-500">
            Aún no tienes movimientos registrados.
          </p>
        ) : (
          <ul className="divide-y divide-background-300/25">
            {movements.map((m) => {
              const meta = MOVEMENT_META[m.kind];
              return (
                <li key={m.id} className="px-4 py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex items-start gap-2.5">
                    <span className={`w-7 h-7 shrink-0 rounded-md flex items-center justify-center ${meta.badge}`}>
                      <i className={`${meta.icon} text-sm`} />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground-950 break-words">
                        {m.productName || meta.label}
                      </p>
                      <p className="text-[10px] text-foreground-500 mt-0.5">
                        {meta.label} · {formatDate(m.date)}
                        {m.qty > 0 ? ` · ${formatNumber(m.qty)} und` : ""}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`text-sm font-bold whitespace-nowrap ${
                      m.kind === "pago" ? "text-accent-200" : "text-foreground-950"
                    }`}
                  >
                    {m.amount > 0 ? formatCOP(m.amount) : "—"}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdvisorAccountPanel;